import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const SquarePaymentPortal = ({ 
  squareConfig, 
  onCardReady, 
  onError,
  containerId = 'square-payment-portal'
}) => {
  const [portalContainer, setPortalContainer] = useState(null);
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const cardInstanceRef = useRef(null);
  const paymentsInstanceRef = useRef(null);
  const mountedRef = useRef(true);

  // Create portal container outside React's control
  useEffect(() => {
    // Create a container that React doesn't manage
    const container = document.createElement('div');
    container.id = containerId;
    container.style.cssText = `
      width: 100%;
      min-width: 480px;
      min-height: 120px;
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
    `;
    
    setPortalContainer(container);
    
    return () => {
      // Cleanup: remove from DOM if it exists
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, [containerId]);

  // Load Square SDK
  useEffect(() => {
    if (window.Square) {
      setIsSDKLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => {
      console.log('Square SDK loaded in portal');
      setIsSDKLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Square SDK in portal');
      onError('Failed to load payment system');
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove script as other components might use it
    };
  }, [onError]);

  // Initialize Square when both SDK and config are ready
  useEffect(() => {
    if (!isSDKLoaded || !squareConfig?.applicationId || !squareConfig?.locationId || !portalContainer) {
      return;
    }

    let timeoutId;

    const initializeSquare = async () => {
      try {
        if (!mountedRef.current) return;

        console.log('Initializing Square in portal...');
        
        // Clear any existing content
        portalContainer.innerHTML = '';

        // Create Square payments instance
        const payments = window.Square.payments(squareConfig.applicationId, squareConfig.locationId);
        paymentsInstanceRef.current = payments;
        
        if (!mountedRef.current) return;

        // Create card instance
        const card = await payments.card();
        cardInstanceRef.current = card;
        
        if (!mountedRef.current) return;

        // Attach to portal container
        await card.attach(portalContainer);
        console.log('Square card attached to portal');
        
        // Notify parent that card is ready
        onCardReady(card);

      } catch (error) {
        console.error('Error initializing Square in portal:', error);
        if (mountedRef.current) {
          onError('Failed to initialize payment form');
        }
      }
    };

    timeoutId = setTimeout(initializeSquare, 100);

    return () => {
      clearTimeout(timeoutId);
      mountedRef.current = false;
      
      if (cardInstanceRef.current) {
        try {
          cardInstanceRef.current.destroy();
          console.log('Square card destroyed in portal');
        } catch (e) {
          console.log('Error destroying card in portal:', e);
        }
        cardInstanceRef.current = null;
      }
      
      paymentsInstanceRef.current = null;
    };
  }, [isSDKLoaded, squareConfig, portalContainer, onCardReady, onError]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Render portal content (empty, Square will populate it)
  return portalContainer ? ReactDOM.createPortal(
    <div style={{ 
      width: '100%', 
      minHeight: '120px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {!cardInstanceRef.current && (
        <span style={{ color: '#666', fontSize: '14px' }}>
          Loading payment form...
        </span>
      )}
    </div>,
    portalContainer
  ) : null;
};

export default SquarePaymentPortal;